import React from "react";
import { IoAlertCircleSharp } from "react-icons/io5";

const BurnoutRedFlags = ({ scoreAllList, userName, createdAt }) => {
  const getStatus = (name, fieldName) => {
    const item = scoreAllList?.find((ele) => ele.name === name);
    return item ? item[fieldName] : "";
  };

  const dimensions = [
    {
      dimension: "Physical",
      max: 50,
      accentColor: "border-[#006494]",
      headerBg: "bg-[#006494]/5",
      scoreColor: "text-[#006494]",
      iconHexColor: "#006494",
      flags: [
        "Chronic reliance on caffeine or sugar to start and sustain the day.",
        "Persistent sleep debt - waking unrefreshed, hitting snooze, crashing in the afternoon.",
        "Exercise and movement are the first things dropped when work intensifies.",
        "Working long, unbroken seated stretches with no recovery pauses.",
        "Frequent illness, tension headaches, or lingering physical fatigue.",
      ],
    },
    {
      dimension: "Emotional",
      max: 50,
      accentColor: "border-[#A84B2F]",
      headerBg: "bg-[#A84B2F]/5",
      scoreColor: "text-[#A84B2F]",
      iconHexColor: "#A84B2F",
      flags: [
        "Increasing irritability, cynicism, or a short fuse with colleagues and family.",
        "Emotional flatness or detachment - little joy, little distress, just numbness.",
        "Inability to disconnect; checking messages compulsively during off-hours.",
        "Withdrawing from renewing relationships and enjoyable activities.",
        "Slow recovery from setbacks - minor frustrations linger for hours or days.",
      ],
    },
    {
      dimension: "Mental",
      max: 50,
      accentColor: "border-[#20808D]",
      headerBg: "bg-[#20808D]/5",
      scoreColor: "text-[#20808D]",
      iconHexColor: "#20808D",
      flags: [
        "Constant task-switching and an inability to sustain focus on one thing.",
        "Reactivity - the day is run by the inbox rather than by chosen priorities.",
        "Procrastination on important work paired with busyness on trivial tasks.",
        "Mental fog, forgetfulness, or a persistent feeling of being scattered.",
        "Difficulty switching off - the mind keeps churning on work after hours.",
      ],
    },
    {
      dimension: "Spiritual",
      max: 50,
      accentColor: "border-[#7A39BB]",
      headerBg: "bg-[#7A39BB]/5",
      scoreColor: "text-[#7A39BB]",
      iconHexColor: "#7A39BB",
      flags: [
        "A growing sense that the work feels meaningless or disconnected from purpose.",
        "Daily actions consistently misaligned with stated values and priorities.",
        "Going through the motions - effort without engagement or conviction.",
        "Loss of intrinsic motivation; only external rewards keep you going.",
        "Rarely pausing to reflect on whether your energy matches who you want to be.",
      ],
    },
  ];

  // Add score/status dynamically
  const reports = dimensions.map((item) => ({
    ...item,
    score: getStatus(item.dimension, "score"),
    status: getStatus(item.dimension, "status"),
    statusClass: getStatus(item.dimension, "colorClass"),
  })).sort((b, a) => (a.score - b.score));

  // Split into pages (2 sections/page)
  const pages = [];
  for (let i = 0; i < reports.length; i += 2) {
    pages.push(reports.slice(i, i + 2));
  }

  return (
    <>
      {pages.map((page, pageIndex) => (
        <div key={pageIndex} className="p-10 px-6 bg-white">
          <div className="px-12 space-y-6 page-break">
            {/* Header */}
            <div className="flex items-center justify-between w-full pt-4 pb-2 border-b text-[#212121]/85 italic">
              <h1 className="text-lg font-semibold">Energy Profile Report</h1>
              <p className="text-lg font-semibold">{userName} | {createdAt}</p>
            </div>

            {/* Title */}
            <div className="mt-6 mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
                {"Burnout Red Flags " + (pageIndex > 0 ? "(Continued)" : "")}

              </h1>

              <p className="text-base text-gray-600 mt-2 font-medium">
                High-signal warning patterns of depletion and imbalance - by
                dimension.
              </p>

              <p className="text-base text-gray-600 mt-1">
                Watch for these patterns. The more that resonate in a dimension,
                the more urgent the recovery investment.
              </p>
            </div>

            {/* Sections */}
            <div className="space-y-10">
              {page.map((section, idx) => (
                <div key={idx} className="space-y-3">
                  {/* Header */}
                  <div
                    className={`flex justify-between items-center p-4 border-l-4 ${section.accentColor} ${section.headerBg}`}
                  >
                    <div>
                      <span
                        className={`block text-sm uppercase font-medium tracking-wider ${section.scoreColor}`}
                      >
                        Dimension
                      </span>

                      <span className="text-2xl font-bold text-gray-800">
                        {section.dimension}
                      </span>
                    </div>

                    <div className="flex items-center gap-4">
                      <span className="text-xl font-bold text-gray-800">
                        <span className={section.scoreColor}>
                          {section.score}
                        </span>
                        <span className="text-gray-400 font-bold text-sm">
                          /{section.max}
                        </span>
                      </span>

                      <span
                        className={`text-sm font-bold px-3 py-1 w-32 text-center rounded border tracking-wider ${section.statusClass}`}
                      >
                        {section.status}
                      </span>
                    </div>
                  </div>

                  {/* Flags */}
                  <div className="divide-y divide-gray-100 pl-4">
                    {section.flags.map((flag, flagIdx) => (
                      <div
                        key={flagIdx}
                        className="flex items-start gap-3 py-3 text-base text-gray-800 leading-relaxed"
                      >
                        <IoAlertCircleSharp
                          className="text-xl flex-shrink-0 mt-0.5"
                          style={{ color: section.iconHexColor }}
                        />
                        <span>{flag}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

export default BurnoutRedFlags;